/* Kidify: "Frequently added together" on the product page (sections/kidify-fbt).
 *
 * 1. When the block nears the screen, ask the recommendations endpoint for the
 *    complementary picks (set in Search & Discovery), rendered by this section.
 *    If there are none, use the fallback rows the section put in a <template>.
 * 2. Keep the count and total in step with the ticked boxes.
 * 3. "Add selected to cart" adds every ticked variant in one /cart/add.js call
 *    and dispatches Shopify's cart event, as the product form does, so the cart
 *    icon and drawer update.
 * 4. When the shopper picks another variant above, re-render the "This item"
 *    row for it through the Section Rendering API (no prices worked out here).
 */

class KidifyFbt extends HTMLElement {
  connectedCallback() {
    if (this.started) return;
    this.started = true;
    this.max = Number(this.dataset.maxPicks) || 3;
    this.rows = this.querySelector('[data-kfbt-rows]');
    this.button = this.querySelector('[data-kfbt-add]');
    this.status = this.querySelector('[data-kfbt-status]');
    this.addEventListener('change', (event) => {
      if (event.target.matches('.kidify-fbt__check')) this.update();
    });
    this.button.addEventListener('click', () => this.add());
    this.onProductSelect = this.onProductSelect.bind(this);
    document.addEventListener('shopify:product:select', this.onProductSelect);

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            this.observer.disconnect();
            this.load();
          }
        },
        { rootMargin: '600px 0px' }
      );
      // Until loaded the element is a 1px invisible strip (kidify-fbt--loading).
      this.observer.observe(this);
    } else {
      this.load();
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    document.removeEventListener('shopify:product:select', this.onProductSelect);
  }

  async load() {
    let picks = [];
    try {
      const response = await fetch(this.dataset.recsUrl);
      if (response.ok) {
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        picks = [...doc.querySelectorAll('[data-kfbt-picks] [data-kfbt-row]')];
      }
    } catch {
      /* fall back below */
    }
    if (!picks.length) {
      const fallback = this.querySelector('template[data-kfbt-fallback]');
      picks = fallback ? [...fallback.content.querySelectorAll('[data-kfbt-row]')] : [];
    }
    picks = picks.slice(0, this.max);
    if (!picks.length) {
      this.hidden = true;
      return;
    }

    for (const row of picks) this.rows.append(document.importNode(row, true));
    this.classList.remove('kidify-fbt--loading');
    this.update();
  }

  ticked() {
    return [...this.querySelectorAll('[data-kfbt-rows] .kidify-fbt__check:checked:not(:disabled)')];
  }

  money(cents) {
    const format = this.dataset.moneyFormat || '${{amount}}';
    const amount = (cents / 100).toFixed(2);
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      const withCommas = (s) => s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      if (key === 'amount_no_decimals') return withCommas(String(Math.round(cents / 100)));
      if (key === 'amount_with_comma_separator') return amount.replace('.', ',');
      return withCommas(amount);
    });
  }

  update() {
    const on = this.ticked();
    const cents = on.reduce((sum, box) => sum + Number(box.dataset.price || 0), 0);
    this.querySelector('[data-kfbt-count]').textContent = `${on.length} ${on.length === 1 ? 'item' : 'items'}`;
    this.querySelector('[data-kfbt-total]').textContent = this.money(cents);
    this.button.disabled = on.length === 0 || this.busy;
  }

  say(message, isError = false) {
    this.status.textContent = message;
    this.status.classList.toggle('kidify-fbt__status--error', isError);
  }

  async add() {
    const on = this.ticked();
    if (!on.length || this.busy) return;
    this.busy = true;
    this.button.setAttribute('aria-busy', 'true');
    this.update();
    this.say('');

    const items = on.map((box) => ({ id: Number(box.value), quantity: 1 }));
    let deferred = null;
    let CartLinesUpdateEvent = null;
    try {
      ({ CartLinesUpdateEvent } = await import('@shopify/events'));
      deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: 1 })),
          promise: deferred.promise,
        })
      );
    } catch {
      /* events module unavailable: the add still works, the cart catches up on the next page */
    }

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const body = await response.json();
      const cart = await fetch(`${root}cart.js`).then((r) => r.json());
      deferred?.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'kidify-fbt', itemCount: items.length, didError: !response.ok },
      });
      if (!response.ok) {
        this.say(body.description || body.message || 'Could not add these to your cart.', true);
      } else {
        this.say(`Added ${items.length} ${items.length === 1 ? 'item' : 'items'} to your cart.`);
      }
    } catch (error) {
      deferred?.reject(error);
      this.say('Could not add these to your cart. Please try again.', true);
    } finally {
      this.busy = false;
      this.button.removeAttribute('aria-busy');
      this.update();
    }
  }

  onProductSelect(event) {
    if (!(event.target instanceof Element) || event.target.closest('product-card, kidify-fbt')) return;
    event.promise
      ?.then(({ detail }) => {
        const id = detail?.resource?.id;
        const current = this.querySelector('[data-kfbt-current] .kidify-fbt__check');
        if (id && current && String(id) !== current.value) this.refreshCurrent(id);
      })
      .catch(() => {});
  }

  async refreshCurrent(variantId) {
    try {
      const url = `${this.dataset.productUrl}?variant=${encodeURIComponent(variantId)}&section_id=${encodeURIComponent(this.dataset.sectionId)}`;
      const html = await fetch(url).then((r) => (r.ok ? r.text() : ''));
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-kfbt-current]');
      const old = this.querySelector('[data-kfbt-current]');
      if (fresh && old) {
        old.replaceWith(document.importNode(fresh, true));
        this.update();
      }
    } catch {
      /* keep the old row; the main form still adds the right variant */
    }
  }
}

if (!customElements.get('kidify-fbt')) customElements.define('kidify-fbt', KidifyFbt);
